import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, ArrowRight } from "lucide-react";

const STORAGE_KEY = "metroshine.memorialDayBanner.dismissed.v1";
// Memorial Day 2026 is Monday, May 25. Promo runs through end of day June 1 ET (one week after).
const ENDS_AT = new Date("2026-06-02T04:00:00Z").getTime();

const useCountdown = (target: number) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const ms = Math.max(0, target - now);
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  const secs = Math.floor((ms % 60_000) / 1000);
  return { ms, days, hours, mins, secs };
};

const pad = (n: number) => n.toString().padStart(2, "0");

const MemorialDayBanner = () => {
  const [visible, setVisible] = useState(false);
  const { ms, days, hours, mins, secs } = useCountdown(ENDS_AT);

  useEffect(() => {
    if (Date.now() >= ENDS_AT) return;
    const dismissed = typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY) === "1";
    if (!dismissed) setVisible(true);
  }, []);

  // Push the fixed Navbar down while the banner is visible so it doesn't overlap.
  useEffect(() => {
    const root = document.documentElement;
    if (visible) {
      const apply = () => root.style.setProperty("--promo-offset", window.innerWidth < 640 ? "44px" : "40px");
      apply();
      window.addEventListener("resize", apply);
      return () => {
        window.removeEventListener("resize", apply);
        root.style.removeProperty("--promo-offset");
      };
    }
    root.style.removeProperty("--promo-offset");
  }, [visible]);

  const dismiss = () => {
    setVisible(false);
    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
  };

  if (ms <= 0) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
          className="fixed top-0 left-0 right-0 z-[60] text-foreground"
          role="region"
          aria-label="Memorial Day promotion"
        >
          <div
            className="relative overflow-hidden border-b border-primary/30"
            style={{
              background:
                "linear-gradient(90deg, hsl(220 60% 9%) 0%, hsl(0 0% 5%) 35%, hsl(0 0% 5%) 65%, hsl(0 65% 14%) 100%)",
            }}
          >
            {/* Subtle stars texture */}
            <div
              className="absolute inset-0 opacity-[0.18] pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 12% 50%, hsl(var(--gold)) 0.5px, transparent 1px), radial-gradient(circle at 27% 30%, hsl(var(--gold)) 0.5px, transparent 1px), radial-gradient(circle at 42% 70%, hsl(var(--gold)) 0.5px, transparent 1px), radial-gradient(circle at 63% 25%, hsl(var(--gold)) 0.5px, transparent 1px), radial-gradient(circle at 78% 65%, hsl(var(--gold)) 0.5px, transparent 1px), radial-gradient(circle at 91% 40%, hsl(var(--gold)) 0.5px, transparent 1px)",
                backgroundSize: "200px 80px",
              }}
            />
            {/* Gold sweep */}
            <motion.div
              aria-hidden
              className="absolute inset-y-0 w-1/3 pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, transparent, hsl(var(--gold) / 0.18), transparent)",
              }}
              animate={{ x: ["-50%", "250%"] }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            />

            <div className="container-luxury px-4 md:px-8 py-2.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.22em] uppercase text-primary">
                  <Star size={11} className="fill-primary" />
                  Memorial Day
                </span>
                <span className="hidden sm:block w-px h-4 bg-primary/30" />
                <p className="text-[12px] sm:text-[13px] font-medium truncate">
                  <span className="gold-gradient-text-animated font-bold">15% OFF</span>{" "}
                  every detail —{" "}
                  <span className="font-mono text-primary tracking-widest">MEMORIAL15</span>
                  <span className="hidden md:inline text-muted-foreground">
                    {" "}· Military & vets save 20% with{" "}
                    <span className="font-mono text-primary tracking-wider">HEROES20</span>
                  </span>
                </p>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                <div className="hidden md:flex items-center gap-1.5" aria-label="Time remaining">
                  {[
                    { v: days, l: "D" },
                    { v: hours, l: "H" },
                    { v: mins, l: "M" },
                    { v: secs, l: "S" },
                  ].map((u) => (
                    <div
                      key={u.l}
                      className="text-[10px] tabular-nums font-mono px-1.5 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary min-w-[34px] text-center"
                    >
                      <span className="font-bold tracking-wider">{pad(u.v)}</span>
                      <span className="text-muted-foreground/70 ml-0.5">{u.l}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/book"
                  className="inline-flex items-center gap-1.5 gold-gradient-bg text-primary-foreground px-3 sm:px-4 py-1.5 text-[11px] sm:text-[12px] font-semibold tracking-wide rounded-full hover:shadow-lg hover:shadow-primary/30 transition-all"
                  onClick={() => {
                    // remember intent so banner stays out of the way after click
                    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
                  }}
                >
                  Claim
                  <ArrowRight size={12} />
                </Link>

                <button
                  onClick={dismiss}
                  aria-label="Dismiss Memorial Day banner"
                  className="w-7 h-7 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-card/50 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MemorialDayBanner;
